import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MonthStatusEnum } from 'src/shared/enums';
import { SetParentHomeworkReservationInActiveMonth } from './../actions/orders.action';

import { IAvailableEventsDTOIn, IBookedEventsByMonth, IEvent, IEventDTOIn, IMonthlyEventData } from '../../interfaces/event.interface';
import {
    IBookedHomeworkSchool,
    IBookedHomeworksSchoolDTOIn,
    IHomeworkSchoolSetting,
    ITotalOfBookedHomeworksPerMonth,
} from '../../interfaces/homework-school.interface';
import { HomeworkSchoolService } from '../../services/homework-school.service';
import { MappersService } from '../../services/mappers.service';
import { getMonthTrad, isDate1BeforeDate2, parseDDMMYYYToDate } from '../../utils/date.utils';
import {
    ActivateHomeworkSchool,
    BookHomeworkSchool,
    CancelHomeworkSchoolBooking,
    DisableHomeworkSchool,
    DisableHomeworkSchoolFullMonth,
    GetAllAvailableHomeworkSchool,
    GetBookedHomeworkSchool,
    GetSchoolHomeworkSchoolSettings,
    ResetFilteredBookedHomeworks,
    ResetHomeworkSchoolData,
    UpdateHomeworkSchoolSetting,
} from '../actions/homework-school.action';
import { GetTotalOfBookedHomeworkPerMonth } from './../actions/homework-school.action';
import { AuthState } from './auth.state';

export interface HomeworkSchoolStateModel {
    schoolHomeworkSchoolSettings: IHomeworkSchoolSetting;
    isLoaded: boolean;
    homeworkSchoolData: IMonthlyEventData[];
    bookedHomeworkSchool: IBookedEventsByMonth[];
    filteredBookedHomeworkSchool: IBookedHomeworkSchool[];
}

@State<HomeworkSchoolStateModel>({
    name: 'homeworkSchool',
    defaults: {
        schoolHomeworkSchoolSettings: null,
        isLoaded: false,
        homeworkSchoolData: [],
        bookedHomeworkSchool: [],
        filteredBookedHomeworkSchool: [],
    },
})
@Injectable()
export class HomeworkSchoolState {
    constructor(private homeworkSchoolService: HomeworkSchoolService, private store: Store, private mapperService: MappersService) {}

    @Selector()
    public static schoolHomeworkSchoolSettings(state: HomeworkSchoolStateModel): IHomeworkSchoolSetting {
        return state.schoolHomeworkSchoolSettings;
    }

    @Selector()
    public static getIsHomeworkSchoolSettingsLoaded(state: HomeworkSchoolStateModel): boolean {
        return state.isLoaded;
    }

    @Selector()
    public static homeworkSchoolData(state: HomeworkSchoolStateModel): IMonthlyEventData[] {
        return state.homeworkSchoolData;
    }

    @Selector()
    public static monthlyHomeworkSchoolData(state: HomeworkSchoolStateModel): IMonthlyEventData[] {
        return state.homeworkSchoolData;
    }

    @Selector()
    public static bookedHomeworkSchool(state: HomeworkSchoolStateModel): IBookedEventsByMonth[] {
        return state.bookedHomeworkSchool;
    }

    @Selector()
    public static filteredBookedHomeworkSchool(state: HomeworkSchoolStateModel): IBookedHomeworkSchool[] {
        return state.filteredBookedHomeworkSchool;
    }

    @Selector()
    public static hasActiveMonth(state: HomeworkSchoolStateModel): boolean {
        return state.homeworkSchoolData?.some((d: IMonthlyEventData) => d.status === MonthStatusEnum.OPEN);
    }

    @Action(GetSchoolHomeworkSchoolSettings)
    public getSchoolHomeworkSchoolSettings(
        ctx: StateContext<HomeworkSchoolStateModel>,
        action: GetSchoolHomeworkSchoolSettings
    ): Observable<IHomeworkSchoolSetting> {
        return this.homeworkSchoolService.getSchoolHomeworkSchoolSettings(action.schoolId).pipe(
            tap((result: IHomeworkSchoolSetting) => {
                if (result) {
                    ctx.patchState({
                        schoolHomeworkSchoolSettings: result,
                        isLoaded: true,
                    });
                }
            })
        );
    }

    @Action(UpdateHomeworkSchoolSetting)
    public updateHomeworkSchoolSetting(
        ctx: StateContext<HomeworkSchoolStateModel>,
        action: UpdateHomeworkSchoolSetting
    ): Observable<IHomeworkSchoolSetting> {
        return this.homeworkSchoolService.patchSchoolHomeworkSchoolSetting(action.homeworkSchoolSetting).pipe(
            tap((result: IHomeworkSchoolSetting) => {
                if (result) {
                    ctx.patchState({
                        schoolHomeworkSchoolSettings: result,
                    });
                }
            })
        );
    }

    @Action(GetAllAvailableHomeworkSchool)
    public getAllAvailableHomeworkSchool(ctx: StateContext<HomeworkSchoolStateModel>): Observable<IMonthlyEventData[]> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.homeworkSchoolService.getAllAvailableHomeworkSchool(schoolId).pipe(
            tap((result: IMonthlyEventData[]) => {
                if (result) {
                    ctx.patchState({
                        homeworkSchoolData: result,
                    });
                }
            })
        );
    }

    @Action(ActivateHomeworkSchool)
    public activateHomeworkSchool(
        ctx: StateContext<HomeworkSchoolStateModel>,
        action: ActivateHomeworkSchool
    ): Observable<IAvailableEventsDTOIn> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.homeworkSchoolService.activateHomeworkSchool(action.homeworkSchool, schoolId).pipe(
            tap((result: IAvailableEventsDTOIn) => {
                if (result) {
                    result.homeworks.forEach((homeworkSchool: IEventDTOIn) => {
                        const state: HomeworkSchoolStateModel = ctx.getState();
                        const homeworkDate: Date = parseDDMMYYYToDate(homeworkSchool.date);
                        const monthIndex: number = homeworkDate.getMonth();
                        const minDays: number = state.schoolHomeworkSchoolSettings.min_days;
                        const maxActivationDate: Date = new Date();
                        maxActivationDate.setDate(maxActivationDate.getDate() + minDays);
                        const isHomeworkInBookingDelay: boolean =
                            isDate1BeforeDate2(homeworkDate, maxActivationDate) && isDate1BeforeDate2(new Date(), homeworkDate);
                        const currentHomeworkStatus: MonthStatusEnum = state.homeworkSchoolData.find(
                            (m: IMonthlyEventData) => m.month === monthIndex
                        ).status;
                        ctx.setState(
                            patch({
                                homeworkSchoolData: updateItem<IMonthlyEventData>(
                                    (monthData: IMonthlyEventData) => monthData.month === monthIndex,
                                    patch({
                                        status: isHomeworkInBookingDelay ? MonthStatusEnum.OPEN : currentHomeworkStatus,
                                        homeworkSchool: append<IEvent>([{ id: homeworkSchool.id, date: homeworkDate }] as IEvent[]),
                                    })
                                ),
                            })
                        );
                    });
                }
            })
        );
    }

    @Action(DisableHomeworkSchool)
    public disableHomeworkSchool(ctx: StateContext<HomeworkSchoolStateModel>, action: DisableHomeworkSchool): Observable<void> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.homeworkSchoolService.disableHomeworkSchool(action.homeworkSchool, schoolId).pipe(
            tap(() => {
                action.homeworkSchool.homework.dates.forEach((date: string) => {
                    const state: HomeworkSchoolStateModel = ctx.getState();
                    const monthIndex: number = parseDDMMYYYToDate(date).getMonth();
                    const isLastHomework: boolean =
                        state.homeworkSchoolData?.find((m) => m.month === monthIndex).homeworkSchool?.length === 1;
                    const currentHomeworkStatus: MonthStatusEnum = state.homeworkSchoolData.find(
                        (m: IMonthlyEventData) => m.month === monthIndex
                    ).status;
                    ctx.setState(
                        patch({
                            homeworkSchoolData: updateItem<IMonthlyEventData>(
                                (monthData: IMonthlyEventData) => monthData.month === monthIndex,
                                patch({
                                    status: isLastHomework ? MonthStatusEnum.INACTIVE : currentHomeworkStatus,
                                    homeworkSchool: removeItem<IEvent>(
                                        (homeworkSchool: IEvent) =>
                                            homeworkSchool.date.toLocaleDateString() === parseDDMMYYYToDate(date).toLocaleDateString()
                                    ),
                                })
                            ),
                        })
                    );
                });
            })
        );
    }

    @Action(DisableHomeworkSchoolFullMonth)
    public disableHomeworkSchoolFullMonth(
        ctx: StateContext<HomeworkSchoolStateModel>,
        action: DisableHomeworkSchoolFullMonth
    ): Observable<void> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.homeworkSchoolService.disableFullMonthHomeworkSchool(action.monthDate, schoolId).pipe(
            tap(() => {
                const monthIndex: number = action.monthDate.getMonth();
                ctx.setState(
                    patch({
                        homeworkSchoolData: updateItem<IMonthlyEventData>(
                            (monthData: IMonthlyEventData) => monthData.month === monthIndex,
                            patch({
                                status: MonthStatusEnum.INACTIVE,
                                homeworkSchool: [] as IEvent[],
                            })
                        ),
                    })
                );
            })
        );
    }

    @Action(ResetHomeworkSchoolData)
    public resetHomeworkSchoolData(ctx: StateContext<HomeworkSchoolStateModel>): void {
        ctx.patchState({
            homeworkSchoolData: null,
            isLoaded: false,
        });
    }

    @Action(BookHomeworkSchool)
    public bookHomeworkSchool(
        ctx: StateContext<HomeworkSchoolStateModel>,
        action: BookHomeworkSchool
    ): Observable<IBookedHomeworksSchoolDTOIn> {
        const state: HomeworkSchoolStateModel = ctx.getState();
        return this.homeworkSchoolService.bookHomeworkSchool(action.homeworkSchoolToBook).pipe(
            tap((result: IBookedHomeworksSchoolDTOIn) => {
                const newBookedHomeworkSchoolByMonth: IBookedEventsByMonth[] = this.mapperService.fromBookedHomeworkSchoolToBookedHomeworkSchoolByMonth(
                    result.book_homeworks
                );
                newBookedHomeworkSchoolByMonth.forEach((monthData: IBookedEventsByMonth) => {
                    if (monthData.bookedHomeworkSchool.length > 0) {
                        ctx.setState(
                            patch({
                                bookedHomeworkSchool: updateItem<IBookedEventsByMonth>(
                                    (month: IBookedEventsByMonth) => month.monthNumber === monthData.monthNumber,
                                    patch({
                                        bookedHomeworkSchool: append(monthData.bookedHomeworkSchool),
                                    })
                                ),
                            })
                        );
                        // Si réservation dans le dernier mois actif,
                        // on met à jour le status des réservations de mois actif dans Order State
                        // (ce qui mettra à jour le "new" dans la side-nav)
                        if (
                            state.homeworkSchoolData.find(
                                (d: IMonthlyEventData) => d.month === monthData.monthNumber && d.status === MonthStatusEnum.OPEN
                            ) &&
                            state.homeworkSchoolData.find(
                                (d: IMonthlyEventData) =>
                                    (d.month === monthData.monthNumber + 1 || (d.month === 0 && monthData.monthNumber === 11)) &&
                                    (d.status === MonthStatusEnum.INACTIVE || d.status === MonthStatusEnum.CLOSED)
                            )
                        ) {
                            this.store.dispatch(new SetParentHomeworkReservationInActiveMonth());
                        }
                    }
                });
            })
        );
    }

    @Action(GetBookedHomeworkSchool)
    public getBookedHomeworkSchool(
        ctx: StateContext<HomeworkSchoolStateModel>,
        action: GetBookedHomeworkSchool
    ): Observable<IBookedHomeworksSchoolDTOIn> {
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        return this.homeworkSchoolService.getBookedHomeworkSchool({ ...action.payload, selectedStudentId }).pipe(
            tap((result: IBookedHomeworksSchoolDTOIn) => {
                if (result) {
                    const newBookedHomeworksByMonth: IBookedEventsByMonth[] = this.mapperService.fromBookedHomeworkSchoolToBookedHomeworkSchoolByMonth(
                        result.book_homeworks
                    );
                    if (action.isFilter) {
                        const filteredHomeworks: IBookedHomeworkSchool[] = [];
                        newBookedHomeworksByMonth.forEach((month: IBookedEventsByMonth) => {
                            Array.prototype.push.apply(filteredHomeworks, month.bookedHomeworkSchool);
                        });
                        ctx.patchState({
                            filteredBookedHomeworkSchool: filteredHomeworks,
                        });
                    } else {
                        newBookedHomeworksByMonth.forEach((monthData: IBookedEventsByMonth) => {
                            if (monthData.bookedHomeworkSchool.length > 0) {
                                ctx.setState(
                                    patch({
                                        bookedHomeworkSchool: updateItem<IBookedEventsByMonth>(
                                            (event: IBookedEventsByMonth) => event.monthNumber === monthData.monthNumber,
                                            patch({ ...event, bookedHomeworkSchool: monthData.bookedHomeworkSchool })
                                        ),
                                    })
                                );
                            }
                        });
                    }
                }
            })
        );
    }

    @Action(GetTotalOfBookedHomeworkPerMonth)
    public getTotalOfBookedHomeworkPerMonth(ctx: StateContext<HomeworkSchoolStateModel>): Observable<ITotalOfBookedHomeworksPerMonth[]> {
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        return this.homeworkSchoolService.getTotalOfBookedHomeworksPerMonth(selectedStudentId).pipe(
            tap((result: ITotalOfBookedHomeworksPerMonth[]) => {
                if (result) {
                    const bookedEvents: IBookedEventsByMonth[] = result.map((month: ITotalOfBookedHomeworksPerMonth) => ({
                        monthNumber: month.month - 1,
                        monthName: getMonthTrad(month.month - 1),
                        bookedHomeworkSchool: [],
                        eventsCount: {
                            total: month.homeworks_count,
                            primary: month.primary_homeworks_count,
                            kindergarten: month.kindergarten_homeworks_count,
                        },
                    }));
                    ctx.patchState({
                        bookedHomeworkSchool: bookedEvents,
                    });
                }
            })
        );
    }

    @Action(CancelHomeworkSchoolBooking)
    public cancelHomeworkSchoolBooking(ctx: StateContext<HomeworkSchoolStateModel>, action: CancelHomeworkSchoolBooking): Observable<void> {
        return this.homeworkSchoolService.cancelHomeworkSchool(action.idsToCancel).pipe(
            tap(() => {
                action.idsToCancel.book_homework_ids.forEach((idToCancel: number) => {
                    ctx.setState(
                        patch({
                            bookedHomeworkSchool: updateItem<IBookedEventsByMonth>(
                                (month: IBookedEventsByMonth) =>
                                    month.bookedHomeworkSchool.findIndex((toFind: IBookedHomeworkSchool) => toFind.id === idToCancel) > -1,
                                patch({
                                    bookedHomeworkSchool: removeItem(
                                        (homeworkSchool: IBookedHomeworkSchool) => homeworkSchool.id === idToCancel
                                    ),
                                })
                            ),
                        })
                    );
                });
            })
        );
    }

    @Action(ResetFilteredBookedHomeworks)
    public resetFilteredBookedHomeworks(ctx: StateContext<HomeworkSchoolStateModel>): void {
        ctx.patchState({
            filteredBookedHomeworkSchool: [],
        });
    }
}
